/**
 * 外部服務連結的組裝（Google 地圖、Google 日曆）。
 *
 * 這些連結都是純前端組出來的，不需要 API key、不會送任何資料給 Google ——
 * 使用者點下去之前，Google 完全不知道這場活動的存在。
 */

import type { Attendee, Flight } from "./types";

/**
 * Google 地圖搜尋連結。
 *
 * 用「店名 + 地址」一起搜尋，比只用地址準：
 * 台灣的地址常有分店（例如「台中PARK2店」），只丟地址會指到錯的點。
 * 兩者都空的話回傳 null，呼叫端就不要顯示連結。
 */
export function mapsUrl(flight: Flight): string | null {
  const query = [flight.venueName, flight.venueAddress]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(" ");
  if (!query) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/** 把 ISO 時間轉成 Google 日曆要的 YYYYMMDDTHHMMSSZ 格式 */
function gcalStamp(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

/**
 * 「加入 Google 日曆」連結。
 *
 * 沒有結束時間的活動預設抓 2 小時 —— 日曆事件一定要有結束時間，
 * 給 0 長度的話 Google 會當成全天事件，在行事曆上看起來很怪。
 */
export function googleCalendarUrl(flight: Flight, attendee?: Attendee): string {
  const start = new Date(flight.departAt);
  const end = flight.endAt ? new Date(flight.endAt) : new Date(start.getTime() + 2 * 60 * 60 * 1000);

  const boardingAt = new Date(start.getTime() - flight.boardingMinutes * 60_000);
  const details = [
    `航班 ${flight.code}`,
    attendee?.seat ? `座位 ${attendee.seat}` : attendee?.status === "standby" ? "候補中" : null,
    `登機門 ${flight.gate}`,
    `建議入場 ${boardingAt.toLocaleTimeString("zh-TW", { hour: "2-digit", minute: "2-digit", hour12: false })}`,
    attendee ? `登機證代碼 ${attendee.passToken}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: flight.title,
    dates: `${gcalStamp(start.toISOString())}/${gcalStamp(end.toISOString())}`,
    location: [flight.venueName, flight.venueAddress].filter(Boolean).join(" "),
    details,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
